/* eslint-disable no-unused-expressions */
import React, { useContext, useEffect, useRef } from "react";

import { Preview, TextState } from "@creatomate/preview";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";

interface VttTextProps {
  preview: Preview;
  subtitleElement: TextState;
}

const VttText: React.FC<VttTextProps> = ({ preview, subtitleElement }) => {
  const {
    data,
    handleUpdateData,
    getActiveElement,
    handleAutosaveDraft
  } = useContext(CreatomateContext);
  const { currentSubtitleId, subtitles, updateSubtitles, activeElementsId } = data;

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const activeElement = getActiveElement(preview);

    if (ref.current && activeElement?.source.id === subtitleElement.source.id) {
      const focusedElement = document.activeElement?.id;

      const moveCursorAtEnd = (div: HTMLDivElement) => {
        const moveAfterRender = () => {
          if (!div.lastChild) {
            requestAnimationFrame(moveAfterRender);
            return;
          }

          const range = document.createRange();
          const selection = window.getSelection();
          range.setStart(div, div.childNodes.length);
          range.collapse(true);
          selection?.removeAllRanges();
          selection?.addRange(range);
        };

        requestAnimationFrame(moveAfterRender);
      };

      if (!focusedElement) {
        ref.current.focus();
        moveCursorAtEnd(ref.current);
      } else if (!focusedElement.includes(subtitleElement.source.id)) {
        ref.current.focus();
        moveCursorAtEnd(ref.current);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeElementsId]);

  useEffect(() => {
    if (updateSubtitles && currentSubtitleId === subtitleElement.source.id) {
      setTimeout(() => {
        handleUpdateSection();
        if (ref.current) ref.current.blur();
        handleUpdateData({ updateSubtitles: false });
      }, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSubtitles]);

  useEffect(() => {
    if (ref.current) {
      let textWithBreaks = (subtitleElement.source.text || "").replace(/\n/g, "<br>");
      if (!textWithBreaks.endsWith("<br>")) {
        textWithBreaks += "<br>";
      }
      ref.current.innerHTML = textWithBreaks;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSubtitleElement = async (): Promise<string> => {
    if (!ref.current) return "";

    let html = ref.current.innerHTML;

    html = html.replace(/<br\s*\/?>(?!$)/gi, "\n");
    html = html.replace(/<div>(.*?)<\/div>/gi, (_match, p1) => `\n${p1}`);

    // Remove any remaining HTML tags
    const tempElement = document.createElement("div");
    tempElement.innerHTML = html;
    const decodedText = tempElement.innerText;
    await preview.applyModifications({ [`${subtitleElement.source.id}.text`]: decodedText });
    return decodedText;
  };

  const handleInput = () => {
    if (ref.current) {
      const lastNode = ref.current.lastChild;
      if (!lastNode || lastNode.nodeName !== "BR") {
        const br = document.createElement("br");
        ref.current.appendChild(br);
      }
    }
  };

  const handleEditText = async (_event: React.FormEvent<HTMLDivElement>) => {
    await updateSubtitleElement();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      if (event.shiftKey || event.ctrlKey) {
        event.preventDefault();
        if (ref.current) {
          const selection = window.getSelection();
          if (!selection) return;
          const range = selection.getRangeAt(0);
          const br = document.createElement("br");
          range.insertNode(br);

          // Create an empty text node to position the cursor after the <br>
          const emptyText = document.createTextNode("");
          range.setStartAfter(br);
          range.insertNode(emptyText);
          range.setStartAfter(emptyText);
          range.setEndAfter(emptyText);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } else {
        event.preventDefault();
        if (ref.current) ref.current.blur();
      }
    }
  };

  const handleFocusSection = async () => {
    handleUpdateData({ currentSubtitleId: subtitleElement.source.id, updateSubtitles: false });
    preview.setActiveElements([subtitleElement.source.id]);
  };

  const handleUpdateSection = async () => {
    const decodedText = await updateSubtitleElement();

    // update section
    handleAutosaveDraft({
      debouncedSubtitlesHandler: true,
      subtitles: subtitles.map(subtitle => {
        if (!subtitle.active) return subtitle;
        return {
          ...subtitle,
          sections: subtitle.sections?.map(section => {
            if (section.creatomateId !== subtitleElement.source.id) return section;
            return { ...section, text: decodedText, originalText: decodedText };
          })
        };
      })
    });
  };

  return (
    <div
      id={`text-${subtitleElement.source.id}`}
      className="VTT-Container__Text"
      ref={ref}
      role="textbox"
      contentEditable
      onBlur={handleUpdateSection}
      onFocus={handleFocusSection}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    />
  );
};

export default VttText;
